import { useState, useRef, useEffect, useLayoutEffect, useId } from 'react'
import { ChevronDown, Check } from '../icons/index.js'
import styles from './Select.module.scss'

/**
 * Custom single-select dropdown built to match the Input primitive visually.
 *
 * Uses a `<div role="combobox">` trigger (not a native `<select>`) so the
 * border and focus ring live on the wrapper — consistent with Input.
 * A hidden native `<select>` is included for form submission.
 *
 * Options can be an array of strings or `{ value, label }` objects.
 *
 * @param {object}   props
 * @param {string|null}  props.value              - Current selected value, or null/undefined for none.
 * @param {(v: string|null) => void} props.onChange
 * @param {Array<string|{value:string, label:string}>} props.options
 * @param {string}  [props.placeholder='Wybierz…']
 * @param {boolean} [props.isActive=false]         - Thick accent border when actively filtering.
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.preferDropUp=false]     - Prefer opening upward when there is enough space.
 * @param {string}  [props.id]
 * @param {string}  [props.name]                   - Falls back to id for form submission.
 * @param {string}  [props.className]              - Extra class on the wrapper.
 */
export function Select({
  value,
  onChange,
  options = [],
  placeholder = 'Wybierz…',
  isActive = false,
  disabled = false,
  preferDropUp = false,
  id,
  name,
  className,
}) {
  const fallbackId = useId()
  const fieldId = id ?? fallbackId

  const [isOpen, setIsOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const [dropUp, setDropUp] = useState(false)
  const wrapperRef = useRef(null)
  const dropdownRef = useRef(null)

  // ── Normalize options ────────────────────────────────────────────────────
  const normalized = options.map((opt) =>
    typeof opt === 'string' || typeof opt === 'number'
      ? { value: String(opt), label: String(opt) }
      : { value: String(opt.value), label: opt.label },
  )

  const selectedOpt = normalized.find((o) => o.value === String(value ?? '')) ?? null

  // ── Helpers ──────────────────────────────────────────────────────────────
  function openList() {
    if (disabled) return
    // Start highlight on the currently selected option.
    setHighlighted(selectedOpt ? normalized.indexOf(selectedOpt) : -1)
    setIsOpen(true)
  }

  function closeList() {
    setIsOpen(false)
    setHighlighted(-1)
  }

  function selectOption(v) {
    onChange?.(v)
    closeList()
    // Return focus to wrapper after selection.
    wrapperRef.current?.focus()
  }

  // ── Click-outside closes ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return
    function onOutside(e) {
      if (!wrapperRef.current?.contains(e.target)) closeList()
    }
    document.addEventListener('mousedown', onOutside)
    return () => document.removeEventListener('mousedown', onOutside)
  }, [isOpen])

  // ── Flip dropdown above wrapper if it would overflow viewport bottom ─────
  useLayoutEffect(() => {
    if (!isOpen || !dropdownRef.current || !wrapperRef.current) return

    const dropdownRect = dropdownRef.current.getBoundingClientRect()
    const wrapperRect = wrapperRef.current.getBoundingClientRect()

    if (preferDropUp) {
      const spaceAbove = wrapperRect.top
      const spaceBelow = window.innerHeight - wrapperRect.bottom
      const fitsAbove = spaceAbove >= dropdownRect.height + 8
      const fitsBelow = spaceBelow >= dropdownRect.height + 8

      setDropUp(fitsAbove || (!fitsBelow && spaceAbove > spaceBelow))
      return
    }

    setDropUp(dropdownRect.bottom > window.innerHeight - 8)
  }, [isOpen, preferDropUp])

  // ── Keyboard navigation ──────────────────────────────────────────────────
  function handleKeyDown(e) {
    if (disabled) return
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (!isOpen) openList()
        else if (highlighted >= 0) selectOption(normalized[highlighted].value)
        break
      case 'ArrowDown':
        e.preventDefault()
        if (!isOpen) openList()
        else setHighlighted((h) => Math.min(h + 1, normalized.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (isOpen) setHighlighted((h) => Math.max(h - 1, 0))
        break
      case 'Escape':
        if (isOpen) { e.preventDefault(); closeList() }
        break
      case 'Tab':
        if (isOpen) closeList()
        break
    }
  }

  const listId = `${fieldId}-list`

  return (
    <div
      ref={wrapperRef}
      id={fieldId}
      className={[
        styles.wrapper,
        isOpen ? styles.open : '',
        isActive ? styles.active : '',
        disabled ? styles.disabled : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      aria-controls={listId}
      aria-activedescendant={highlighted >= 0 ? `${fieldId}-opt-${highlighted}` : undefined}
      tabIndex={disabled ? -1 : 0}
      // Inline outline:0 kills WP admin CSS which applies outline on :focus
      // with specificity high enough to beat CSS module classes.
      style={{ outline: 0 }}
      onClick={() => (isOpen ? closeList() : openList())}
      onKeyDown={handleKeyDown}
    >
      {/* Hidden native <select> for form submission — not visible or interactive. */}
      <select
        name={name ?? id}
        value={value ?? ''}
        onChange={() => {}}
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
      >
        {normalized.map((o) => (
          <option key={o.value} value={o.value}>
            {typeof o.label === 'string' ? o.label : o.value}
          </option>
        ))}
      </select>

      {/* ── Trigger text ──────────────────────────────────────────────────── */}
      <span
        className={[styles.trigger, !selectedOpt ? styles.placeholder : '']
          .filter(Boolean)
          .join(' ')}
      >
        {selectedOpt?.label ?? placeholder}
      </span>

      {/* ── Chevron suffix ────────────────────────────────────────────────── */}
      <span className={styles.chevron} aria-hidden="true">
        <ChevronDown />
      </span>

      {/* ── Dropdown list ─────────────────────────────────────────────────── */}
      {isOpen && (
        <ul
          ref={dropdownRef}
          role="listbox"
          id={listId}
          className={[styles.dropdown, dropUp ? styles.dropUp : ''].filter(Boolean).join(' ')}
          onMouseLeave={() => setHighlighted(-1)}
        >
          {normalized.map((opt, i) => (
            <li
              key={opt.value}
              id={`${fieldId}-opt-${i}`}
              role="option"
              aria-selected={opt.value === String(value ?? '')}
              className={[
                styles.option,
                opt.value === String(value ?? '') ? styles.optionSelected : '',
                i === highlighted ? styles.optionHighlighted : '',
              ]
                .filter(Boolean)
                .join(' ')}
              // Inline padding: WP reset `:is([data-app='bim-cadaplus-app'].bim-app-root) li { padding: 0 }`
              // has specificity (1,1,1) which beats `.option` (0,1,0). Inline always wins.
              style={{ padding: '0.5em 0.75em' }}
              onMouseEnter={() => setHighlighted(i)}
              // mouseDown + preventDefault keeps focus on the wrapper (blur closes list race).
              onMouseDown={(e) => {
                e.preventDefault()
                selectOption(opt.value)
              }}
            >
              <span className={styles.optionLabel}>{opt.label}</span>
              <span className={styles.optionCheck} aria-hidden="true">
                {opt.value === String(value ?? '') && <Check width="0.875em" height="0.875em" />}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
